// RecoveryRequest — route /auth/recovery (public, unauthenticated).
//
// The landing the sign-in MFA challenge's "Account recovery →" link opens
// (D15/R17). A user who has lost access to all their MFA methods enters their
// email + one recovery code; we POST /innovateflow/auth/recovery, which
// validates + reserves the code and EMAILS a confirmation link. This page never
// mints tokens and never signs anyone in — it only kicks off the email-confirmed
// two-step break-glass. iflow palette; light theme only.
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { KeyRound, Loader2, MailCheck } from 'lucide-react'
import { requestRecovery } from '../../services/api'

function errMsg(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { message?: string; error?: string } } }).response
    const m = resp?.data?.message || resp?.data?.error
    if (m) return m
  }
  return e instanceof Error ? e.message : fallback
}

export default function RecoveryRequest() {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (!email.trim() || !code.trim()) { setError('Enter your email and one recovery code.'); return }
    setBusy(true)
    try {
      await requestRecovery(email.trim(), code.trim())
      setSent(true)
    } catch (err) {
      setError(errMsg(err, 'Could not start account recovery. Check your details and try again.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-sky-50 via-white to-[#E8EFF4]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-iflow-500 to-iflow-700 mx-auto mb-3 flex items-center justify-center text-white text-xl font-bold">iF</div>
          <h1 className="text-2xl font-bold text-iflow-900">Account recovery</h1>
          <p className="text-sm text-slate-500 mt-1">Lost access to all your two-factor methods?</p>
        </div>

        <div className="bg-white rounded-2xl border border-[rgba(12,74,110,0.12)] p-8 shadow-xl">
          {sent ? (
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3"><MailCheck size={22} /></div>
              <h2 className="text-lg font-semibold text-iflow-900">Check your email</h2>
              <p className="text-sm text-slate-600 mt-2">
                If that email and recovery code are valid, we&apos;ve sent a confirmation link. Open it to remove your
                second factor — then sign in with your password and set MFA up again.
              </p>
              <p className="text-xs text-slate-600 mt-3">The link expires in 15 minutes. You can close this tab.</p>
              <Link to="/login" className="inline-block mt-5 text-sm font-medium text-iflow-600 hover:text-iflow-700">← Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="flex items-center gap-2 text-iflow-700 mb-1"><KeyRound size={18} /><span className="text-sm font-semibold">Verify with a recovery code</span></div>
              <p className="text-xs text-slate-500 -mt-1">
                We&apos;ll email you a confirmation link rather than signing you in here — this keeps your account safe even
                if a code is intercepted.
              </p>

              {error && <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

              <div>
                <label htmlFor="rec-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input id="rec-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900" placeholder="you@example.com" required />
              </div>
              <div>
                <label htmlFor="rec-code" className="block text-sm font-medium text-slate-700 mb-1">Recovery code</label>
                <input id="rec-code" type="text" value={code} onChange={(e) => setCode(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 font-mono tracking-wider" placeholder="XXXX-XXXX" required />
              </div>

              <button type="submit" disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700 disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />} Email me a recovery link
              </button>
              <p className="text-center text-xs text-slate-600">
                Remembered a method? <Link to="/login" className="text-iflow-600 font-medium hover:text-iflow-700">Back to sign in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
